import React, { useState, useEffect } from 'react';
import { usePWAInstall } from '../contexts/PWAInstallContext';
import { Button } from './Button';
import { theme } from '../constants';
import { messagingService } from '../firebase/messagingService';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { useWindowSize } from '../hooks/useWindowSize';

const PWAInstallBanner: React.FC = () => {
    const { canInstallPWA, triggerInstallPrompt } = usePWAInstall();
    const [isVisible, setIsVisible] = useState(false);
    const [wasDismissedPermanently, setWasDismissedPermanently] = useState(false);
    const userEmail = useSelector((state: RootState) => state.userSlice.user?.email);
    const { width } = useWindowSize();
    const isNarrow = width <= 420;

    const BANNER_DISMISSED_KEY = 'pwaInstallBannerDismissed';
    const SHOW_BANNER_DELAY = 30000; // 30 seconds

    useEffect(() => {
        const dismissed = localStorage.getItem(BANNER_DISMISSED_KEY);
        if (dismissed === 'true') {
            setWasDismissedPermanently(true);
        }
    }, []);

    useEffect(() => {
        if (canInstallPWA && !wasDismissedPermanently) {
            const timer = setTimeout(() => {
                setIsVisible(true);
            }, SHOW_BANNER_DELAY);
            return () => clearTimeout(timer);
        } else {
            setIsVisible(false);
        }
    }, [canInstallPWA, wasDismissedPermanently]);

    const handleInstallClick = async () => {
        const { outcome } = await triggerInstallPrompt();
        if (outcome === 'accepted') {
            setIsVisible(false);
            localStorage.setItem(BANNER_DISMISSED_KEY, 'true');
            setWasDismissedPermanently(true);
        }
    };

    const handleEnableNotifications = async () => {
        handleDismiss(true); // Dismiss the banner permanently
        if (userEmail) {
            try {
                await messagingService.requestPermission(userEmail);
                // You might want to show a success message to the user here
            } catch (error) {
                console.error('Notification permission request failed:', error);
                // Optionally, inform the user that it failed
            }
        } else {
            console.warn('User email not available to request notification permission.');
        }
    };

    const handleDismiss = (permanently: boolean = false) => {
        setIsVisible(false);
        if (permanently) {
            localStorage.setItem(BANNER_DISMISSED_KEY, 'true');
            setWasDismissedPermanently(true);
        }
    };

    if (!isVisible || !canInstallPWA || wasDismissedPermanently) {
        return null;
    }

    return (
        <div style={styles.banner}>
            <button
                onClick={() => handleDismiss(false)}
                style={styles.closeButton}
                aria-label="Close banner"
                onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = '1';
                    e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.08)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = '0.7';
                    e.currentTarget.style.backgroundColor = 'transparent';
                }}
            >
                <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M12 4L4 12M4 4L12 12"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </button>

            <div style={styles.leftSection}>
                <div style={styles.iconWrap}>
                    <img src="/qache_logo_192.png" alt="Qache" style={{ width: 24, height: 24, objectFit: 'contain' }} />
                </div>
                <p style={styles.text}>Install our app to be notified when you win!</p>
            </div>

            <div style={{ ...styles.buttonGroup, flexDirection: isNarrow ? 'column' : 'row', alignItems: isNarrow ? 'stretch' : 'center' }}>
                <Button
                    title="Install"
                    onClick={handleInstallClick}
                    style={styles.buttonPrimary}
                    containerStyle={isNarrow ? { width: '100%' } : undefined}
                />
                <Button
                    title="Enable Notifications"
                    onClick={handleEnableNotifications}
                    style={styles.buttonSecondary}
                    containerStyle={isNarrow ? { width: '100%' } : undefined}
                />
            </div>
        </div>
    );
};

const styles: { [key: string]: React.CSSProperties } = {
    banner: {
        position: 'fixed',
        bottom: 'calc(100px + env(safe-area-inset-bottom, 0px))',
        left: '50%',
        transform: 'translateX(-50%)',
        width: 'calc(100% - 40px)',
        maxWidth: '600px',
        backgroundImage: `linear-gradient(135deg, ${theme.colors.mainColor} 0%, ${theme.colors.darkYellow} 100%)`,
        padding: '20px 24px',
        paddingRight: '52px',
        borderRadius: '16px',
        boxShadow: '0 10px 24px rgba(0,0,0,0.15)',
        border: '1px solid rgba(255,255,255,0.4)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'stretch',
        zIndex: 1050, // Ensure it's above most other content
        color: theme.colors.textColor,
        flexWrap: 'wrap', // Allow wrapping on small screens
        gap: '14px', // Gap between text and button group if they wrap
    },
    leftSection: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        flex: 1,
        minWidth: 0,
    },
    iconWrap: {
        width: 40,
        height: 40,
        borderRadius: '50%',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 3px 8px rgba(0,0,0,0.12)',
        flexShrink: 0,
        minWidth: 40,
        minHeight: 40,
        aspectRatio: '1',
    },
    text: {
        margin: 0,
        marginRight: '12px',
        fontSize: '1rem',
        lineHeight: 1.4,
        ...theme.fonts.Poppins_600SemiBold,
        color: theme.colors.mainDark,
        textAlign: 'left',
        whiteSpace: 'normal',
        overflow: 'visible',
        textOverflow: 'clip',
    },
    buttonGroup: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        flexShrink: 0,
        width: '100%',
    },
    buttonPrimary: {
        padding: '12px 20px',
        backgroundColor: theme.colors.mainDark,
        color: theme.colors.white,
        border: 'none',
        borderRadius: 999,
        cursor: 'pointer',
        fontSize: '0.95rem',
        ...theme.fonts.Poppins_600SemiBold,
        boxShadow: '0 6px 16px rgba(4,3,37,0.25)'
    },
    buttonSecondary: {
        padding: '12px 20px',
        backgroundColor: 'rgba(255,255,255,0.95)',
        color: theme.colors.mainDark,
        border: '1px solid rgba(255,255,255,0.95)',
        borderRadius: 999,
        cursor: 'pointer',
        fontSize: '0.95rem',
        ...theme.fonts.Poppins_600SemiBold,
        boxShadow: '0 4px 12px rgba(0,0,0,0.12)'
    },
    closeButton: {
        position: 'absolute',
        top: '12px',
        right: '12px',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: '5px',
        color: theme.colors.mainDark,
        borderRadius: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: 0.7,
        transition: 'opacity 0.2s ease',
        zIndex: 1,
    },
};

export default PWAInstallBanner; 